import APP_CONFIG from '@/config/app';
import { motion } from 'framer-motion';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
type AnimationContextType = {
  back: boolean;
  setBack: (back: boolean) => void;
  direction: 'open' | 'close';
  setDirection: (direction: 'open' | 'close') => void;
};
const defaultValue: AnimationContextType = {
  back: false,
  setBack: () => {},
  direction: 'open',
  setDirection: () => {},
};
const AnimationContext = createContext<AnimationContextType>(defaultValue);
export const useAnimationLayout = () => {
  const context = useContext(AnimationContext);
  if (!context) {
    throw new Error(
      'useAnimationLayout must be used within an AnimationProvider',
    );
  }
  return context;
};
export const AnimationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [back, setBack] = useState(false);
  const [direction, setDirection] = useState<'open' | 'close'>('open');
  return (
    <AnimationContext.Provider
      value={{ back, setBack, direction, setDirection }}
    >
      {children}
    </AnimationContext.Provider>
  );
};
const AnimatedLayout = ({
  children,
  className,
  slideFrom = 'right',
  uniqueKey: key,
}: {
  children: ReactNode;
  className?: string;
  slideFrom?: 'right' | 'left';
  uniqueKey?: string | number | undefined;
}) => {
  const { setBack } = useAnimationLayout();
  const slideDirection = slideFrom.toUpperCase() as 'LEFT' | 'RIGHT';
  const directionConfig =
    APP_CONFIG.ANIMATION_SETTINGS.SLIDE_FROM[slideDirection];
  useEffect(() => {
    setBack(false);
  }, [setBack]);
  return (
    <motion.div
      className={`${className || ''} w-full h-full z-10`}
      initial={directionConfig.initial}
      animate={directionConfig.open}
      exit={directionConfig.closed}
      transition={APP_CONFIG.ANIMATION_SETTINGS.COMMON}
      key={key}
    >
      {children}
    </motion.div>
  );
};
export default React.memo(AnimatedLayout);
